// File: src/components/ChatUI/ChatUI.tsx

import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  FC
} from "react";
import { createRoot } from "react-dom/client";
import "./Chat.css";
import Input from "./InputSection/Input";
import { FormattedRes } from "./FormattedRes/FormattedRes";
import { ProfileLogo } from "../Layout/LayoutSections/HelpSection/HelpSection";
import { AcceptPolicies } from "./AcceptPolicies/AcceptPolicies";
import { ChatButton } from "../../generalComponents/Buttons/ChatButton";
import { DISCLAIMER_TEXT } from "../../../constants";
import { bot } from "../../../utils";
import { ChatData } from "./utilsLogic/ChatData";
import { Message } from "../MainPage";
import { v4 } from "uuid";
import {
  handleButtonClick,
  handleInput,
  stopFetchingHandler
} from "./utilsLogic/utils";
import ChartsContainer from "../../generalComponents/Chart/ChartsContainer";
import { logDebug } from "../../../utils/logger";
import { useAppContext, AppProvider } from "../../../AppDataContext";
import { arrowDown, roundForArrowDown } from "../../../utils";
import GraphemeSplitter from "grapheme-splitter";

interface ChatUIProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  policiesNotAllow: boolean;
  setVersions: React.Dispatch<React.SetStateAction<boolean>>;
  userId: string;
  userClass: string;
  client_id: string;
  client_token: string;
  startNewChat: () => void;
  badgeurl?: string;
  article_index?: string;
  follow_up_question?: string;
  proxy?: string;
  welcome_message?: string;
}

const ChatUI: FC<ChatUIProps> = (props) => {
  const chatData: any = ChatData(props);
  const {
    messages,
    sendQuestion,
    isPress,
    onInputChange,
    userInput,
    setUserInput,
    setMessages,
    setLoad,
    load,
    fetchData,
    setFetchData,
    policiesAccepted,
    setPoliciesAccepted,
    handleScroll = () => {},
    regenerateResponse = () => {},
    resetUserInput = () => { setUserInput(""); }
  } = chatData;

  const justify: string = chatData.justify || "flex-start";
  const childrenHeight: number = chatData.childrenHeight ?? 1;
  const setChildrenHeight: React.Dispatch<React.SetStateAction<number>> =
    chatData.setChildrenHeight || (() => {});
  const error1: any = chatData.error1 || null;
  const error2: any = chatData.error2 || null;
  const error3: any = chatData.error3 || null;
  const isDesktopScreen: boolean = chatData.isDesktopScreen ?? true;
  const currentIndex: number = chatData.currentIndex ?? 0;
  const setError1: React.Dispatch<React.SetStateAction<any>> = chatData.setError1 || (() => {});
  const setError2: React.Dispatch<React.SetStateAction<any>> = chatData.setError2 || (() => {});
  const setError3: React.Dispatch<React.SetStateAction<any>> = chatData.setError3 || (() => {});
  const showInitialQuestions: boolean = chatData.showInitialQuestions ?? true;
  const paramsWrapForStatistics: any = chatData.paramsWrapForStatistics || {};

  const defaultListRef = useRef<HTMLDivElement>(null);
  const listRef = chatData.listRef || defaultListRef;
  const defaultInputRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = chatData.inputRef || defaultInputRef;

  const AppDataContext = useAppContext();

  // NEW: State for controlling the fake verbos experience.
  const [stopStreaming, setStopStreaming] = useState(false);
  
  // -----------------------------
  // BADGE URL – DECLARED EARLY TO USE IN DEPENDENCIES.
  // -----------------------------
  const imageSrcbadgeUrl = useMemo(() => {
    if (props.badgeurl && props.badgeurl.toLowerCase().startsWith("http")) {
      return props.badgeurl;
    }
    const badgeUrlValue = bot[props.badgeurl as keyof typeof bot];
    if (Array.isArray(badgeUrlValue)) {
      const dynamicThemelogoUrl = badgeUrlValue.find((url: any) =>
        url.includes(sessionStorage.getItem("userTheme"))
      );
      return dynamicThemelogoUrl ? dynamicThemelogoUrl : badgeUrlValue[0];
    }
    return props.badgeurl && bot[props.badgeurl as keyof typeof bot]
      ? bot[props.badgeurl as keyof typeof bot]
      : bot.default;
  }, [props.badgeurl]);



  // -----------------------------
  // UPDATE IMAGE AND CHART SRC for actions.
  // -----------------------------
  useEffect(() => {
    const updateImageSrc = () => {
      const images = document.querySelectorAll('img[alt^="docimage"]');
      images.forEach((img) => {
        const altText = img.getAttribute("alt");
        const action = AppDataContext.ActionList?.find(
          (a: any) => a.className === altText
        );
       logDebug("action.srcUrl:", action?.srcUrl," altText:", altText);
      if (altText) {
        img.setAttribute("alt", altText.replace("docimage_", ""));
        img.setAttribute("class", altText.replace("docimage_present_", ""));
      }
        if (action && action.srcUrl) {
          img.setAttribute("src", action.srcUrl);
          (img as HTMLImageElement).style.display = "block";
          (img as HTMLImageElement).style.margin = "0 auto";
          (img as HTMLImageElement).style.borderRadius = "0";
        }
      });
    };

    updateImageSrc();
  }, [AppDataContext.ActionList]);


  useEffect(() => {
    const updateChartSrc = () => {
      const containers = document.querySelectorAll('img[alt^="chart"]');
      containers.forEach((container) => {
        if (container && container.isConnected && container.parentNode) {
          const newElement = document.createElement("div");
          const root = createRoot(newElement);
          root.render(
            <AppProvider value={AppDataContext}>
              <ChartsContainer />
            </AppProvider>
          );
          try {
            container.parentNode.replaceChild(newElement, container);
          } catch (err) {
            console.error("Error replacing chart container:", err);
          }
        }
      });
    };

    updateChartSrc();
  }, [AppDataContext.ActionList, messages]);
  // -----------------------------
  // Remove streaming incremental updates.
  // -----------------------------
  useEffect(() => {
    if (messages && messages.length > 0) {
      const lastUserMessageIndex = messages
        .slice()
        .reverse()
        .findIndex((message: any) => message.type === "user");
      if (lastUserMessageIndex !== -1) {
        const botMessageAfterUser = messages
          .slice(messages.length - lastUserMessageIndex)
          .find((message: any) => message.type === "bot");
        if (botMessageAfterUser && botMessageAfterUser.text.includes("<DONE>")) {
          const finalText = botMessageAfterUser.text.replace(/<DONE>/g, "");
          setMessages((prev: Message[]) =>
            prev.map((msg) =>
              msg.id === botMessageAfterUser.id ? { ...msg, text: finalText } : msg
            )
          );
        }
      }
    }
  }, [messages, setMessages]);

  // -----------------------------
  // Clear fake verbos when the actual reply begins streaming.
  // -----------------------------
  useEffect(() => {
    const hasBotText = messages.some(
      (msg: any) => msg.type === "bot" && msg.text.trim().length > 0
    );
    if (hasBotText) {
      chatData.setFakeVerbos("");
    }
  }, [messages]);

  // -----------------------------
  // Auto-scroll: When a new user message arrives, scroll to the bottom.
  // -----------------------------
  useEffect(() => {
    if (listRef && listRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === "user") {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      } else {
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        // const nearBottom = scrollHeight - scrollTop - clientHeight < 50;
        const nearBottom = scrollHeight - scrollTop - clientHeight < 100; // Increased threshold from 50 to 100
        if (nearBottom) {
          listRef.current.scrollTop = scrollHeight;
        }
      }
    }
  }, [messages, listRef]);

  // -----------------------------
  // NEW: State and scroll listener for the arrow button.
  // -----------------------------
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  useEffect(() => {
    const chatContainer = listRef.current;
    if (!chatContainer) return;

    function checkScrollPosition() {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const atBottom = scrollHeight - scrollTop - clientHeight < 80;
      // adjust the scroll button arrow position
      const input = document.querySelector('.input') as HTMLElement
      const roundForArrow = document.querySelector('.roundForArrow') as HTMLElement
      if(input && roundForArrow) {
        // arrow Button - input height
        roundForArrow.style.bottom = `calc( 80px - 48px + ${input.style.height})`;
        logDebug("input offsetHeight:", input.style.height, "roundForArrow offsetHeight:", roundForArrow.style.bottom);
      }
        // Show the arrow button only if not at the bottom
      setShowScrollBottom(!atBottom);
    }

    chatContainer.addEventListener("scroll", checkScrollPosition);
    checkScrollPosition();
    return () => {
      chatContainer.removeEventListener("scroll", checkScrollPosition);
    };
  }, [listRef]);

const scrollToBottom = () => {
  if (listRef.current) {
    const element = listRef.current;
    const targetScrollTop = element.scrollHeight;

    const scrollStep = Math.ceil((targetScrollTop - element.scrollTop) / 20); // Use Math.ceil for fractional steps
    let currentStep = 0;

    const interval = setInterval(() => {
      if (currentStep < 20) {
        element.scrollTop = Math.min(element.scrollTop + scrollStep, targetScrollTop); // Ensure it doesn't overshoot
        currentStep++;
      } else {
        element.scrollTop = targetScrollTop; // Ensure final position is accurate
        clearInterval(interval);
      }
    }, 20); // Adjust the interval time for speed
   }
 };

  // -----------------------------
  // Optional: Page Visibility Listener to auto-scroll when tab becomes active.
  // -----------------------------
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [listRef]);

  // -----------------------------
  // DISCLAIMER TEXT.
  // -----------------------------
  const disclaimerText = useMemo(() => "DISCLAIMER: " + DISCLAIMER_TEXT.join(""), []);

  // -----------------------------
  // RENDER MESSAGES (full responses).
  // -----------------------------
  const memoizedMessages = useMemo(() => {
    return messages
      .map((message: any) => {
        if (message.type === "bot initial-questions" && messages.length > 1 && !showInitialQuestions) {
          return null;
        }
        if (message.type === "user") {
          return (
            <div key={message.id + "-" + message.type} className={message.type}>
              <ProfileLogo profile="UU" />
              <div className="userText">{message.text}</div>
            </div>
          );
        }
        return (
          message.text.trim().length > 0 && (
            <div key={message.id + "-" + message.type} className={message.type}>
              <img src={imageSrcbadgeUrl} alt="bot" width="40px" height="50px" />
              <FormattedRes
                questionHandler={sendQuestion}
                load={load}
                wrappedParams={paramsWrapForStatistics}
                stopStreaming={!load}
              >
                {message.text}
              </FormattedRes>
            </div>

          )
        );
      })
      .filter(Boolean);
  }, [
    messages,
    sendQuestion,
    load,
    showInitialQuestions,
    paramsWrapForStatistics,
    imageSrcbadgeUrl
  ]);

useEffect(() => {
  // Select the element with the "hoverShine" class
  const textElement = document.querySelector(".hoverShine") as HTMLElement | null;

  if (textElement) {
    // Delay execution to ensure the DOM is fully loaded
  const textLength = textElement?.textContent?.length || 0; // Fallback to 0 if invalid

  // Dynamically calculate animation left value based on text length
  const animationLeft = Math.min(50 + (textLength - 9) * 7, 300); // Adjust the multiplier and cap as needed
  // Apply the calculated animation left value as a CSS variable
  textElement.style.setProperty("--animation-left", `${animationLeft}px`);

      // Set a maximum limit for the animation duration
      logDebug("textLength:", textLength);
      textElement.style.setProperty("--text-width", `${animationLeft}px`);
  }
}, [chatData.fakeVerbos]); // Re-run the effect when `chatData.fakeVerbos` changes
const splitter = new GraphemeSplitter();
  // -----------------------------
  // Loading Indicator.
  // -----------------------------
  const loadingIndicator = load ? (
    chatData.isFirstTime ? (
      <div
          key={chatData.fakeVerbos}
          className="fakeVerbos hoverShine" style={{ textAlign: "left", fontSize: "16px"}}>
        {splitter.splitGraphemes(chatData.fakeVerbos).map((char:any, idx:any) => (
          <span key={idx} style={{ animationDelay: `${idx * 0.05}s` }}>
            {char}
          </span>
        ))}
      </div>
    ) : (
      <div className="spinner">Loading...</div>
    )
  ) : null;

  return (
    <div className={`chat ${props.policiesNotAllow ? "chat_policies" : ""}`}>
      <div
        className="screen"
        ref={listRef}
        style={{ justifyContent: justify }}
        onScroll={handleScroll}
      >
        {props.policiesNotAllow && !policiesAccepted ? (
          <>
            <div className="overlay"></div>
            <AcceptPolicies
              setVersions={props.setVersions}
              getMyHeight={setChildrenHeight as React.Dispatch<React.SetStateAction<number>>}
              client_id={props.client_id}
              userId={AppDataContext.userId}
              client_token={AppDataContext.client_token}
              proxy={props.proxy}
              onAccept={() => setPoliciesAccepted(true)}
            />
          </>
        ) : (
          <>
            {memoizedMessages}
            {loadingIndicator}
          </>
        )}
        {error1 && (
          <div className="chat__errorType1">
            <ChatButton
              errorText="Whoops! seems like too many people are using it at the moment."
              displayEnter={isDesktopScreen}
              regenerateResponse={regenerateResponse}
              btnText="Regenerate message"
            />
          </div>
        )}
        {error2 && (
          <div className="chat__errorType2 bot">
            <img
              src={
                props.badgeurl && props.badgeurl !== "none"
                  ? `/assets/img/${props.badgeurl}bot.svg`
                  : "/assets/img/bot.darkTheme.svg"
              }
              alt="bot"
              width="40px"
              height="50px"
            />
            <ChatButton
              errorText="Oops! It looks like the response is taking longer than usual."
              displayEnter={isDesktopScreen}
              regenerateResponse={regenerateResponse}
              btnText="Regenerate message"
            />
          </div>
        )}
        {error3 && (
          <div className="chat__errorType3">
            <ChatButton
              errorText={error3}
              displayEnter={isDesktopScreen}
              regenerateResponse={regenerateResponse}
              btnText="Start a new chat"
            />
          </div>
        )}
      </div>

      {showScrollBottom && (
        <div
          className="roundForArrow"
          onClick={scrollToBottom}
        >
          <img
            className="arrowInsideCircle"
            src={arrowDown}
            alt="Scroll to bottom"
          />
        </div>
      )}

      <section className="inputContainer" ref={inputRef}>
        <Input
          userInput={userInput}
          onInputChange={onInputChange}
          handleInput={(e: any) =>
            handleInput(
              e,
              userInput,
              setMessages,
              setLoad,
              messages,
              load,
              currentIndex,
              setFetchData,
              setStopStreaming,
              setError1,
              setError2,
              setError3,
              AppDataContext.userId,
              AppDataContext.userClass,
              AppDataContext.client_id,
              AppDataContext.client_token,
              AppDataContext.article_index || undefined,
              AppDataContext.follow_up_question || undefined,
              AppDataContext.proxy,
              { CONVERSATION_MESSAGE_SENT: 1 },
              AppDataContext.language_for_chat || "EN",
              AppDataContext
            )
          }
          handleButtonClick={() =>
            !load &&
            handleButtonClick(
              userInput,
              setMessages,
              setLoad,
              messages,
              setFetchData,
              setStopStreaming,
              setError1,
              setError2,
              setError3,
              AppDataContext.userId,
              AppDataContext.userClass,
              AppDataContext.client_id,
              AppDataContext.client_token,
              AppDataContext.article_index,
              AppDataContext.proxy,
              { CONVERSATION_MESSAGE_SENT: 1 },
              AppDataContext.language_for_chat || "EN",
              AppDataContext
            )
          }
          load={load}
          resetUserInput={resetUserInput}
          fetchData={fetchData}
          setFetchData={setFetchData}
          stopFetchingHandler={async () =>
            await stopFetchingHandler(setFetchData, setStopStreaming, setLoad)
          }
          setInputRef={(ref: React.RefObject<HTMLTextAreaElement>) =>
            (inputRef.current = ref.current)
          }
          article_index={props.article_index}
        />
        <div className="tooltip-container">
          <div className="underText truncate-underText-to-1-line" data-tooltip={disclaimerText}>
            <span>
              <b>DISCLAIMER:</b>
            </span>{" "}
            {DISCLAIMER_TEXT}{" "}
          </div>
        </div>
      </section>
    </div>
  );
};

export default React.memo(ChatUI);
